import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Combobox } from "@/components/ui/combobox";
import { toast } from "sonner";
import api from "@/lib/api";
import { Upload, FileText, Loader2 } from "lucide-react";

const Toggle = ({ value, onChange, options, testId }) => (
  <div className="grid grid-cols-2 gap-2" data-testid={testId}>
    {options.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={`border rounded-lg p-2.5 text-sm font-medium transition-colors ${value === v ? "border-[#0F1E3D] bg-[#0F1E3D]/5 text-[#0F1E3D] ring-2 ring-[#C9A227]/50" : "border-slate-200 text-slate-500"}`}>{l}</button>
    ))}
  </div>
);

const ExtraSigners = ({ extra, setExtra, party, tenant, clients }) => {
  const add = (s) => setExtra((l) => [...l, s]);
  const upd = (i, k, v) => setExtra((l) => l.map((s, j) => (j === i ? { ...s, [k]: v } : s)));
  const second = tenant && (tenant.first_name2 || tenant.name2) ? { name: `${tenant.first_name2 || ""} ${tenant.last_name2 || ""}`.trim() || tenant.name2, email: tenant.email2 || "", role: "2e debiteur" } : null;
  const cl = tenant?.client_id ? clients.find((c) => c.id === tenant.client_id) : null;
  return (
    <div className="border rounded-xl p-3 space-y-2" data-testid="signdoc-extra">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-xs text-slate-500">Extra ondertekenaars (tekenen tegelijk, elk via een eigen link)</Label>
        <div className="flex flex-wrap gap-1.5">
          {party === "debtor" && second && <button type="button" className="text-[11px] px-2 py-1 rounded-full border border-[#C9A227]/60 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={() => add(second)} data-testid="signdoc-add-second">+ 2e debiteur</button>}
          {party === "debtor" && cl && <button type="button" className="text-[11px] px-2 py-1 rounded-full border border-[#C9A227]/60 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={() => add({ name: cl.contact_person || cl.name, email: cl.email || "", role: "Opdrachtgever" })} data-testid="signdoc-add-client">+ Opdrachtgever</button>}
          <button type="button" className="text-[11px] px-2 py-1 rounded-full border border-[#C9A227]/60 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={() => add({ name: "", email: "", role: "Ondertekenaar" })} data-testid="signdoc-add-other">+ Andere persoon</button>
        </div>
      </div>
      {extra.map((s, i) => (
        <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_120px_auto] gap-2 items-center rounded-lg bg-[#F7F5EF] border border-[#C9A227]/30 p-2" data-testid={`signdoc-extra-${i}`}>
          <Input value={s.name} onChange={(e) => upd(i, "name", e.target.value)} placeholder="Naam" data-testid={`signdoc-extra-name-${i}`} />
          <Input value={s.email} onChange={(e) => upd(i, "email", e.target.value)} placeholder="E-mailadres" data-testid={`signdoc-extra-email-${i}`} />
          <Input value={s.role} onChange={(e) => upd(i, "role", e.target.value)} placeholder="Rol" data-testid={`signdoc-extra-role-${i}`} />
          <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => setExtra((l) => l.filter((_, j) => j !== i))} data-testid={`signdoc-extra-remove-${i}`}>Verwijderen</button>
        </div>
      ))}
    </div>
  );
};

export default function SignDocDialog({ open, onOpenChange, tenants, clients, onCreated, preset }) {
  const [party, setParty] = useState("debtor");
  const [pid, setPid] = useState("");
  const [source, setSource] = useState("upload");
  const [title, setTitle] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [file, setFile] = useState(null);
  const [content, setContent] = useState("");
  const [extra, setExtra] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { return; }
    setParty(preset?.party || "debtor"); setPid(preset?.id || ""); setSource("upload"); setTitle(""); setTplId(""); setFile(null); setContent(""); setExtra([]);
    api.get("/contract-templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  }, [open, preset]);

  const pickTpl = (id) => { setTplId(id); const t = templates.find((x) => x.id === id); if (t) { setContent(t.content); if (!title) setTitle(t.name); } };
  const upload = async (f) => {
    if (!f) { return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const up = (await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
      const ex = (await api.post("/agreements/extract", { file_url: up.url, file_name: f.name })).data;
      setFile({ url: up.url, name: f.name }); setContent(ex.content);
      if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
      toast.success("Document ingelezen — controleer de tekst");
    } catch (e) { toast.error(e?.response?.data?.detail || "Inlezen mislukt"); }
    setBusy(false);
  };
  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/agreements/custom", { party_type: party, party_id: pid, title, source, template_id: tplId, file_url: file?.url || "", file_name: file?.name || "", content, extra_signers: extra });
      toast.success("Document opgesteld ter ondertekening");
      onCreated(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Opstellen mislukt"); }
    setBusy(false);
  };

  const list = party === "client" ? clients.map((c) => ({ value: c.id, label: c.name })) : tenants.map((t) => ({ value: t.id, label: `${t.first_name || ""} ${t.last_name || ""}`.trim() || t.id }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="signdoc-dialog">
        <DialogHeader><DialogTitle>Document ter ondertekening</DialogTitle><DialogDescription>Upload een Word-document of PDF, of kies een eigen sjabloon. Je kunt de tekst controleren voordat je hem verstuurt.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          <div><Label className="text-xs text-slate-500">Wie tekent?</Label><Toggle value={party} onChange={(v) => { setParty(v); setPid(""); }} options={[["debtor", "Debiteur"], ["client", "Opdrachtgever"]]} testId="signdoc-party" /></div>
          <Combobox testId="signdoc-party-select" value={pid} onChange={setPid} placeholder={party === "client" ? "Kies opdrachtgever" : "Kies debiteur"} options={list} />
          <div><Label className="text-xs text-slate-500">Bron</Label><Toggle value={source} onChange={(v) => { setSource(v); setContent(""); setFile(null); setTplId(""); }} options={[["upload", "Word / PDF uploaden"], ["template", "Eigen sjabloon"]]} testId="signdoc-source" /></div>
          {source === "upload" ? (
            <label className="flex items-center gap-3 border-2 border-dashed border-[#C9A227]/50 rounded-xl p-4 cursor-pointer hover:bg-[#C9A227]/5" data-testid="signdoc-upload">
              {busy ? <Loader2 className="h-5 w-5 animate-spin text-[#C9A227]" /> : <Upload className="h-5 w-5 text-[#C9A227]" />}
              <span className="text-sm text-slate-600">{file ? <><FileText className="h-4 w-4 inline mr-1" />{file.name}</> : "Kies een .docx, .pdf of .txt bestand"}</span>
              <input type="file" accept=".docx,.pdf,.txt" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="signdoc-file-input" />
            </label>
          ) : (
            <Combobox testId="signdoc-template-select" value={tplId} onChange={pickTpl} placeholder={templates.length ? "Kies sjabloon" : "Nog geen sjablonen — maak er een via Sjablonen"} options={templates.map((t) => ({ value: t.id, label: t.name }))} />
          )}
          <div><Label className="text-xs text-slate-500">Titel</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Samenwerkingsovereenkomst" data-testid="signdoc-title" /></div>
          {content && <div><Label className="text-xs text-slate-500">Tekst (aanpasbaar; velden als {"{naam}"} worden automatisch ingevuld)</Label><Textarea value={content} onChange={(e) => setContent(e.target.value)} rows={10} className="text-sm font-mono" data-testid="signdoc-content" /></div>}
          <ExtraSigners extra={extra} setExtra={setExtra} party={party} tenant={party === "debtor" ? tenants.find((t) => t.id === pid) : null} clients={clients} />
          <Button className="w-full bg-[#C9A227] hover:bg-[#B8921F] text-[#0F1E3D] font-semibold" disabled={busy || !pid || !title.trim() || !content.trim() || extra.some((s) => !s.name.trim() || !s.email.includes("@"))} onClick={create} data-testid="signdoc-create">Opstellen</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
